import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Input, Select, Tooltip, notification } from 'antd';
import { ArrowRightOutlined, WarningOutlined } from '@ant-design/icons';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { paths } from 'routes/routes';
import { MIN_REASON } from 'pages/access-groups/common';
import {
  ActionConfig,
  ActionModal,
  DateTimeCell,
  EmptyHint,
  NameList,
  PRIVILEGED_GO,
  PRIVILEGED_WHO,
  PersonCell,
  SOURCE_LABEL,
  departmentText,
  personName,
  privilegedName,
  privilegedWhy,
  readBulk,
  shiftText,
  showBulkResult,
  useStaff,
  useStaffList,
  warningText,
} from './shared';
import DataView, { DataColumn } from './data-view';
import TabHead from './tab-head';
import { ClaimApproveBody, ClaimBulkBody, StaffClaimRow } from './types';
import styles from './styles.module.scss';

const PAGE_SIZE = 20;
const BULK_MAX = 50;
const NOT_VERIFIED_TIP =
  'Người này chưa xác minh email nên chưa duyệt được. Nhắc họ mở email xác minh, hoặc từ chối yêu cầu.';

/** Vì sao nút Duyệt tắt: lý do ngắn máy chủ gửi (`privilegedReason`) + giải thích + việc cần làm. */
const privilegedText = (row: StaffClaimRow) =>
  `${privilegedWhy(row.privilegedReason)} ${PRIVILEGED_GO}`;

/**
 * Dòng cảnh báo dưới "cũ → mới" cho yêu cầu không duyệt được ở đây (Admin, Quản lý, phòng QL Vận
 * Hành), kèm lối sang trang Tài khoản.
 */
const PrivilegedNote = ({ row }: { row: StaffClaimRow }) => (
  <span className={styles.privileged}>
    <WarningOutlined aria-hidden className={styles.privilegedIcon} />
    <span>
      {privilegedWhy(row.privilegedReason)}{' '}
      <Link to={paths.accountManagement}>Mở trang Tài khoản</Link>
    </span>
  </span>
);

/** Thẻ "Chờ duyệt": yêu cầu làm nhân viên / đổi phòng ban đang chờ Admin quyết định. */
const PendingTab = () => {
  const { refreshKey, afterAction, shifts } = useStaff();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<StaffClaimRow[]>([]);
  const [action, setAction] = useState<ActionConfig | null>(null);
  // Ca chọn trong hộp duyệt; để ngoài ActionConfig vì nó đổi khi hộp đang mở.
  const [shiftPick, setShiftPick] = useState<string | undefined>();
  const [approving, setApproving] = useState<StaffClaimRow | null>(null);
  // onSubmit được tạo lúc mở hộp, nên đọc ca mới nhất qua ref.
  const shiftRef = useRef<string | undefined>(undefined);
  shiftRef.current = shiftPick;

  const { items, total, loading, error, reload } = useStaffList<StaffClaimRow>(
    API.staffAccessClaims,
    { status: 'PENDING', page, limit: PAGE_SIZE, q },
    refreshKey
  );

  useEffect(() => setSelected([]), [refreshKey, q]);

  // Xử lý hết các dòng của trang cuối: lùi về trang cuối còn dữ liệu, không để bảng trống.
  useEffect(() => {
    if (!loading && !error && items.length === 0 && total > 0 && page > 1) {
      setPage(Math.max(1, Math.ceil(total / PAGE_SIZE)));
    }
  }, [loading, error, items.length, total, page]);

  const nameById = useMemo(() => {
    const map = new Map<number, string>();
    [...items, ...selected].forEach((row) => map.set(row.id, personName(row.user)));
    return map;
  }, [items, selected]);
  const nameOf = (id: number) => nameById.get(id) || `Yêu cầu số ${id}`;

  const closeAction = () => {
    setAction(null);
    setApproving(null);
  };

  const approve = (row: StaffClaimRow) => {
    setApproving(row);
    // Máy chủ chỉ nhận ca đang chạy của đúng phòng ban mới; ca người gửi chọn mà không hợp lệ
    // thì để trống cho Admin chọn lại.
    const requested = shifts.find(
      (shift) =>
        String(shift.id) === String(row.defaultShift) &&
        shift.isActive !== false &&
        shift.department === row.toDepartment
    );
    setShiftPick(requested ? String(requested.id) : undefined);
    setAction({
      title: `Duyệt ${personName(row.user)} làm nhân viên`,
      okText: 'Duyệt',
      description: (
        <p style={{ marginBottom: 12 }}>
          Duyệt xong, hệ thống đặt phòng ban <strong>{row.toDepartmentLabel}</strong> cho người này,
          ghi nhận họ thuộc công ty và xếp ca làm mặc định. Họ vào được Bảng tin nội bộ và thấy
          Điểm Sao trên app.
        </p>
      ),
      noteLabel: 'Ghi chú (không bắt buộc)',
      noteMin: 0,
      onSubmit: async (note) => {
        const body: ClaimApproveBody = {};
        if (shiftRef.current) body.defaultShift = shiftRef.current;
        if (note) body.note = note;
        const res: any = await AxiosUtils.post(`${API.staffAccessClaims}/${row.id}/approve`, body);
        // Duyệt xong nhưng còn việc cần xem lại (vd. chưa xếp được ca): báo vàng, giữ tới khi đóng.
        const warning = warningText(res?.data?.warning ?? res?.data?.data?.warning);
        if (warning) {
          notification.warning({
            message: `Đã duyệt ${personName(row.user)}, nhưng cần xem lại`,
            description: warning,
            duration: 0,
          });
        } else {
          notification.success({ message: `Đã duyệt ${personName(row.user)}.` });
        }
        afterAction();
      },
    });
  };

  const reject = (row: StaffClaimRow) => {
    // Phòng ban HIỆN TẠI của người gửi (không phải lúc gửi): người chưa có phòng ban thì nói
    // rõ họ vẫn là người dùng thường, thay vì "vẫn giữ phòng ban hiện tại".
    const current = departmentText(row.user.department, row.user.departmentLabel);
    setAction({
      title: `Từ chối yêu cầu của ${personName(row.user)}`,
      okText: 'Từ chối',
      danger: true,
      description: (
        <p style={{ marginBottom: 12 }}>
          {current
            ? `Người này vẫn giữ phòng ban hiện tại (${current}).`
            : 'Người này vẫn là người dùng thường, chưa có phòng ban.'}{' '}
          Họ có thể gửi yêu cầu khác sau.
        </p>
      ),
      noteLabel: `Lý do từ chối (bắt buộc, ít nhất ${MIN_REASON} ký tự)`,
      noteMin: MIN_REASON,
      onSubmit: async (note) => {
        await AxiosUtils.post(`${API.staffAccessClaims}/${row.id}/reject`, { note });
        notification.success({ message: `Đã từ chối yêu cầu của ${personName(row.user)}.` });
        afterAction();
      },
    });
  };

  const bulk = (kind: 'APPROVE' | 'REJECT') => {
    const approveKind = kind === 'APPROVE';
    // Gửi MỌI người đã chọn: máy chủ mới là nơi biết email đã xác minh hay chưa (danh sách trên
    // màn hình có thể đã cũ). Ai bị máy chủ từ chối thì hộp kết quả ghi rõ từng người kèm lý do.
    // Riêng yêu cầu privileged (Admin, Quản lý, phòng QL Vận Hành) thì không gửi duyệt.
    const skipped = approveKind ? selected.filter((row) => row.privileged) : [];
    const rows = approveKind ? selected.filter((row) => !row.privileged) : selected;
    const skippedNames = skipped.map(privilegedName).join(', ');
    const unverified = approveKind ? rows.filter((row) => !row.user.isVerifyAccount).length : 0;
    setAction({
      title: approveKind ? `Duyệt ${rows.length} người` : `Từ chối ${rows.length} yêu cầu`,
      okText: approveKind ? `Duyệt ${rows.length} người` : `Từ chối ${rows.length} yêu cầu`,
      danger: !approveKind,
      description: (
        <>
          <p style={{ marginBottom: 8 }}>
            {approveKind
              ? 'Những người dưới đây sẽ thành nhân viên theo phòng ban và ca họ đã gửi:'
              : 'Yêu cầu của những người dưới đây sẽ bị từ chối, họ giữ phòng ban hiện tại:'}
          </p>
          <NameList names={rows.map((row) => personName(row.user))} />
          {unverified > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${unverified} người trong số này chưa xác minh email nên sẽ không được duyệt. Những người còn lại vẫn được duyệt; kết quả ghi rõ từng người.`}
            />
          )}
          {skipped.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`Không duyệt ở đây ${skipped.length} người: ${skippedNames}.`}
              description={
                <>
                  Họ là {PRIVILEGED_WHO}: việc này liên quan cấp duyệt, nên họ được bỏ ra khỏi lần
                  duyệt này và vẫn nằm trong danh sách chờ. Muốn đổi phòng ban của họ, vào{' '}
                  <Link to={paths.accountManagement}>trang Tài khoản</Link>.
                </>
              }
            />
          )}
        </>
      ),
      noteLabel: approveKind
        ? 'Ghi chú (không bắt buộc)'
        : `Lý do từ chối (bắt buộc, ít nhất ${MIN_REASON} ký tự)`,
      noteMin: approveKind ? 0 : MIN_REASON,
      onSubmit: async (note) => {
        const body: ClaimBulkBody = { ids: rows.map((row) => row.id), action: kind };
        if (note) body.note = note;
        const res = await AxiosUtils.post(`${API.staffAccessClaims}/bulk`, body);
        showBulkResult(
          readBulk(res),
          approveKind ? 'Đã duyệt' : 'Đã từ chối',
          nameOf,
          skipped.length
            ? `Không gửi duyệt ${skipped.length} người: ${skippedNames}. ${PRIVILEGED_GO}`
            : undefined
        );
        afterAction();
      },
    });
  };

  // Nút "Duyệt hàng loạt" chỉ tính người duyệt được ở đây (không phải yêu cầu privileged).
  const eligibleSelected = selected.filter((row) => !row.privileged);
  const verifiedSelected = eligibleSelected.filter((row) => row.user.isVerifyAccount).length;
  let bulkApproveTip: string | undefined;
  if (eligibleSelected.length === 0) {
    bulkApproveTip = `Không ai trong số đã chọn duyệt được ở đây (họ là ${PRIVILEGED_WHO}). ${PRIVILEGED_GO}`;
  } else if (verifiedSelected === 0) {
    bulkApproveTip = 'Chưa ai trong số đã chọn xác minh email.';
  }

  // Tổng bề rộng 1.044px (kể cả cột tích 48px): vừa vùng nội dung 1.046px của màn 1366×768.
  const columns: DataColumn<StaffClaimRow>[] = [
    {
      key: 'user',
      title: 'Người gửi',
      width: 250,
      slot: 'head',
      render: (row) => <PersonCell user={row.user} showRole />,
    },
    {
      key: 'source',
      title: 'Nguồn',
      width: 100,
      render: (row) => SOURCE_LABEL[row.source] || row.source,
    },
    {
      key: 'department',
      title: 'Phòng ban: cũ → mới',
      width: 200,
      render: (row) => (
        <>
          <span className={styles.move}>
            <span className={styles.moveFrom}>
              {departmentText(row.fromDepartment, row.fromDepartmentLabel) || 'Chưa có'}
            </span>
            <ArrowRightOutlined aria-label="đổi sang" style={{ fontSize: 12 }} />
            <span className={styles.moveTo}>
              {departmentText(row.toDepartment, row.toDepartmentLabel)}
            </span>
          </span>
          {row.privileged && <PrivilegedNote row={row} />}
        </>
      ),
    },
    {
      key: 'shift',
      title: 'Ca',
      width: 150,
      render: (row) =>
        row.defaultShift ? (
          shiftText(row.defaultShift, shifts)
        ) : (
          <span className={styles.sub}>Chưa chọn</span>
        ),
    },
    {
      key: 'createdAt',
      title: 'Gửi lúc',
      width: 120,
      render: (row) => <DateTimeCell value={row.createdAt} />,
    },
    {
      key: 'actions',
      title: 'Thao tác',
      width: 176,
      slot: 'actions',
      render: (row) => {
        // Privileged: không duyệt ở đây (xem PrivilegedNote). Chưa xác minh email: chưa duyệt được.
        let blockTip: string | undefined;
        if (row.privileged) blockTip = privilegedText(row);
        else if (!row.user.isVerifyAccount) blockTip = NOT_VERIFIED_TIP;
        return (
          <span className={styles.actions}>
            <Tooltip title={blockTip} trigger={['hover', 'focus']}>
              {/* span bọc ngoài: nút bị vô hiệu không tự phát sự kiện rê chuột / nhận tiêu điểm. */}
              <span tabIndex={blockTip ? 0 : undefined}>
                <Button
                  size="small"
                  type="primary"
                  disabled={!!blockTip}
                  onClick={() => approve(row)}
                >
                  Duyệt
                </Button>
              </span>
            </Tooltip>
            <Button size="small" danger onClick={() => reject(row)}>
              Từ chối
            </Button>
          </span>
        );
      },
    },
  ];

  const departmentShifts = approving
    ? shifts.filter(
        (shift) => shift.isActive !== false && shift.department === approving.toDepartment
      )
    : [];
  const shiftOptions = departmentShifts.map((shift) => ({
    value: String(shift.id),
    label: shift.name,
  }));

  return (
    <div>
      <TabHead />
      <p className={styles.lead}>
        Người tự chọn “nhân viên” lúc đăng ký, hoặc tự đổi phòng ban trong hồ sơ, sẽ nằm ở đây cho
        tới khi Admin duyệt. Trước khi duyệt, vai trò và phòng ban của họ giữ nguyên như dòng “Vai
        trò hiện tại”. Yêu cầu của Admin, Quản lý, hoặc xin vào / ra phòng QL Vận Hành (phòng quyết
        định cấp duyệt) không duyệt ở đây: đổi phòng ban của họ ở trang Tài khoản.
      </p>
      <div className={styles.toolbar}>
        <Input.Search
          allowClear
          className={styles.search}
          placeholder="Tìm theo tên, email hoặc TikTok ID"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onSearch={(value) => {
            setPage(1);
            setQ(value.trim());
          }}
        />
        <div className={styles.bulk} aria-live="polite">
          {selected.length > 0 ? (
            <>
              <span>
                Đã chọn {selected.length}/{BULK_MAX}
              </span>
              <Tooltip title={bulkApproveTip} trigger={['hover', 'focus']}>
                <span tabIndex={bulkApproveTip ? 0 : undefined}>
                  <Button
                    type="primary"
                    disabled={!!bulkApproveTip}
                    onClick={() => bulk('APPROVE')}
                  >
                    Duyệt {eligibleSelected.length} người
                  </Button>
                </span>
              </Tooltip>
              <Button danger onClick={() => bulk('REJECT')}>
                Từ chối {selected.length} yêu cầu
              </Button>
              <Button type="text" onClick={() => setSelected([])}>
                Bỏ chọn
              </Button>
            </>
          ) : (
            <span>
              Tích ô đầu dòng để duyệt hoặc từ chối nhiều người một lần (tối đa {BULK_MAX}).
            </span>
          )}
        </div>
      </div>
      <DataView<StaffClaimRow>
        columns={columns}
        items={items}
        rowKey={(row) => row.id}
        loading={loading}
        error={error}
        onRetry={reload}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPage={setPage}
        selection={{
          selected,
          max: BULK_MAX,
          onChange: setSelected,
          label: (row) => personName(row.user),
        }}
        empty={
          q ? (
            <EmptyHint
              title="Không thấy yêu cầu nào khớp ô tìm"
              text="Thử tìm bằng email hoặc bỏ bớt chữ."
            />
          ) : (
            <EmptyHint
              title="Không có yêu cầu nào đang chờ"
              text="Khi có người xin làm nhân viên hoặc xin đổi phòng ban, yêu cầu sẽ hiện ở đây."
            />
          )
        }
      />
      <ActionModal
        config={action}
        extra={
          approving && shiftOptions.length > 0 ? (
            <div className={styles.field}>
              <label htmlFor="staff-approve-shift">Ca làm mặc định</label>
              <Select
                id="staff-approve-shift"
                style={{ width: '100%' }}
                placeholder="Chọn ca làm"
                value={shiftPick}
                onChange={(value) => setShiftPick(value || undefined)}
                options={shiftOptions}
              />
            </div>
          ) : undefined
        }
        onClose={closeAction}
      />
    </div>
  );
};

export default PendingTab;
